<template>
  <div
    ref="widgetRef"
    class="messenger-widget"
    :class="[
      `messenger-widget--align-${sharedSettings.placement.alignTo}`,
      {
        'messenger-widget--mounted': isMounted,
        'messenger-widget--width-100pr': width100pr,
      },
    ]"
  >
    <div
      v-if="currentViewName !== 'minimized'"
      ref="expandedViewScrollRef"
      class="messenger-widget__view"
      :class="{
        [`messenger-widget__view--${viewAnimationSuffix}`]: viewAnimationSuffix,
        'messenger-widget__view--no-scroll': currentViewName === 'chat',
      }"
    >
      <div
        ref="expandedViewContentRef"
        class="messenger-widget__view-content"
      >
        <slot
          :name="`view-${currentViewName}`"
          v-bind="{ setExpandedViewCustomHeight }"
        />
        <div
          v-if="isPopoverVisible"
          class="messenger-widget__popover-container"
        >
          <slot name="popover" />
        </div>
      </div>
    </div>

    <div class="messenger-widget__button">
      <messenger-widget-minimized-view
        v-if="viewName === 'minimized'"
        ref="minimizedViewRef"
        :view-settings="minimizedViewSettings"
        :is-tooltip-visible="isTooltipVisible"
        :has-notification="hasNotification"
        @expand="$emit('expand')"
        @close-tooltip="$emit('close-tooltip')"
        @start-button-resize="startButtonSize = $event"
      />
      <messenger-start-button
        v-else
        class="messenger-widget-expanded__button"
        :is-expanded="true"
        @click="$emit('minimize')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import MessengerStartButton from '@widgets/messenger/components/MessengerStartButton.vue'
import type {
  MessengerMinimizedViewSettings,
  MessengerResizePayload,
  MessengerSharedSettings,
  MessengerViewName,
} from '@widgets/messenger/types'
import MessengerWidgetMinimizedView from '@widgets/messenger/components/views/MessengerWidgetMinimizedView.vue'
import { useWidgetSizeObserver } from '@widgets/messenger/compositions/useWidgetSizeObserver'
import { messengerWidgetWidth } from '@widgets/messenger/const'
import { provideWidgetSharedSettings } from '@widgets/messenger/compositions/useWidgetSharedSettings'
import { type ComponentPublicInstance, onMounted, watch, watchEffect } from '@/composition/vue/compositionApi'
import { ref } from '@/composition/vue/compositionApi'

const props = defineProps<{
  viewName: MessengerViewName
  sharedSettings: MessengerSharedSettings
  minimizedViewSettings: MessengerMinimizedViewSettings
  isTooltipVisible: boolean
  isPopoverVisible?: boolean
  hasNotification?: boolean
  width100pr?: boolean
}>()

const emit = defineEmits<{
  (e: 'expand'): void
  (e: 'minimize'): void
  (e: 'close-tooltip'): void
  (e: 'resize', payload: MessengerResizePayload): void
}>()

const widgetRef = ref<HTMLElement | null>(null)
const minimizedViewRef = ref<ComponentPublicInstance | null>(null)
const expandedViewScrollRef = ref<HTMLElement | null>(null)
const expandedViewContentRef = ref<HTMLElement | null>(null)

provideWidgetSharedSettings(() => props.sharedSettings)

const isMounted = ref(false)
const currentViewName = ref<MessengerViewName>(props.viewName)
const viewAnimationSuffix = ref<'enter-from' | 'enter-to' | 'leave-from' | 'leave-to' | ''>('')
const transitionDuration = 200
let changeViewTransitionTimeout: ReturnType<typeof setTimeout> | undefined
watch(
  () => props.viewName,
  (newValue, oldValue) => {
    clearTimeout(changeViewTransitionTimeout)
    if (newValue === 'minimized') {
      viewAnimationSuffix.value = 'leave-from'
      setTimeout(() => {
        viewAnimationSuffix.value = 'leave-to'
      }, 1)
      changeViewTransitionTimeout = setTimeout(() => {
        currentViewName.value = newValue
      }, transitionDuration)
    } else if (oldValue === 'minimized') {
      viewAnimationSuffix.value = 'enter-from'
      currentViewName.value = newValue
      setTimeout(() => {
        viewAnimationSuffix.value = 'enter-to'
      }, 1)
    } else {
      currentViewName.value = newValue
    }
  },
)

const startButtonSize = ref({ width: 0, height: 0 })

const { size, setExpandedViewCustomHeight } = useWidgetSizeObserver({
  widgetRef,
  minimizedViewRef,
  expandedViewScrollRef,
  expandedViewContentRef,
  currentViewName,
})

watchEffect(() => {
  emit('resize', {
    ...size.value,
    startButtonSize: {
      width: startButtonSize.value.width,
      height: startButtonSize.value.height,
    },
  })
})

onMounted(() => {
  setTimeout(() => {
    isMounted.value = true
  }, 1)
})
</script>

<style lang="scss" scoped>
@import '@/styles/custom-variables.scss';

.messenger-widget {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 10px;
  width: calc(v-bind(messengerWidgetWidth) * 1px);
  max-width: 100%;
  opacity: 0; // for animation, see modifier `--mounted`
  transition: opacity calc(v-bind(transitionDuration) * 1ms) ease-in-out;

  &__view {
    box-shadow: 0 3px 12px 0 rgba($black, 0.3);
    border-radius: 20px;
    overflow: auto;
    flex-grow: 1;
    flex-shrink: 1;
    min-height: 0;
    align-self: stretch;
    transition:
      transform calc(v-bind(transitionDuration) * 1ms) ease-in-out,
      opacity calc(v-bind(transitionDuration) * 1ms) ease-in-out;

    &--enter-from,
    &--leave-to {
      opacity: 0;
      transform: translateY(50px);
    }

    &--enter-to,
    &--leave-from {
      opacity: 1;
    }

    &--no-scroll {
      display: flex;
      overflow: visible;
    }
  }

  &__view-content {
    position: relative;
    width: 100%;
  }

  &__button {
    flex-grow: 0;
    flex-shrink: 0;
  }

  &--align-left {
    align-items: flex-start;
  }

  &--align-right {
    align-items: flex-end;
  }

  &--mounted {
    opacity: 1;
  }

  &--width-100pr {
    width: 100%;
  }

  &__popover-container {
    position: absolute;
    inset: 0;
  }
}
</style>
